import * as THREE from 'three';
import { makeRng, clamp } from '../core/Rand.js';
import { shotHash } from './Recoil.js';

/**
 * Hitscan + penetration + projectile ballistics.
 *
 * Everything degrades gracefully: `ctx.physics` may still be a stub with only a
 * three.js Raycaster behind `raycast()`, in which case we synthesise `raycastAll` by
 * walking the ray, and read surface names off `userData.surface`.
 */

/** Energy lost per metre of material, in "penetration units". */
export const PENETRATION = {
  concrete: 118, metal: 165, wood: 34, dirt: 62, sand: 74,
  glass: 9, fabric: 6, water: 40, flesh: 18, foliage: 3, default: 100,
};

/** Nominal thickness assumed when we cannot find an exit face. */
const ASSUMED_THICKNESS = {
  concrete: 0.20, metal: 0.006, wood: 0.035, dirt: 0.40, sand: 0.30,
  glass: 0.006, fabric: 0.004, water: 0.6, flesh: 0.25, foliage: 0.02, default: 0.12,
};

const HIT_ZONE_MULT = { head: 3.0, neck: 2.2, chest: 1.0, stomach: 1.05, arm: 0.72, leg: 0.68, foot: 0.6 };

const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _m3 = new THREE.Matrix3();

/** Bring any raycast result into the CONTRACTS.md shape. */
function normalizeHit(h, dir) {
  if (!h) return null;
  const point = h.point ? h.point.clone() : new THREE.Vector3();
  let normal = h.normal ? h.normal.clone() : null;
  if (!normal && h.face && h.object) {
    normal = h.face.normal.clone().applyMatrix3(_m3.getNormalMatrix(h.object.matrixWorld)).normalize();
  }
  if (!normal) normal = dir.clone().negate();
  const object = h.object || null;
  const surface = h.surface || object?.userData?.surface
    || object?.material?.userData?.surface || 'concrete';
  return {
    point, normal, distance: h.distance ?? 0, object, surface,
    entity: h.entity ?? object?.userData?.entity ?? null,
    zone: h.zone ?? object?.userData?.hitZone ?? null,
  };
}

export class Ballistics {
  constructor(ctx) {
    this.ctx = ctx;
    this.projectiles = [];
    this.rng = makeRng(0x8a11);
  }

  get physics() { return this.ctx.physics; }

  /** raycastAll with a walking fallback for stub physics. */
  castAll(origin, dir, far, mask) {
    const P = this.physics;
    if (!P) return [];
    if (typeof P.raycastAll === 'function') {
      const hits = P.raycastAll(origin, dir, far, mask) || [];
      return hits.map((h) => normalizeHit(h, dir)).filter(Boolean);
    }
    if (typeof P.raycast !== 'function') return [];
    const out = [];
    const o = origin.clone();
    let travelled = 0;
    for (let i = 0; i < 10 && travelled < far; i++) {
      const h = normalizeHit(P.raycast(o, dir, far - travelled, mask), dir);
      if (!h) break;
      h.distance = travelled + h.distance;
      out.push(h);
      const step = Math.max(h.distance - travelled, 0) + 0.002;
      travelled += step;
      o.copy(origin).addScaledVector(dir, travelled);
      if (out.length > 1 && out[out.length - 1].distance - out[out.length - 2].distance < 1e-4) break;
    }
    return out;
  }

  /**
   * Fire one hitscan bullet.
   * @param {THREE.Vector3} origin  muzzle position, world space
   * @param {THREE.Vector3} dir     unit direction, already spread-perturbed
   * @param {object} profile        weapon damage profile
   * @param {number} seed
   * @param {number} shotIndex
   */
  hitscan(origin, dir, profile, seed, shotIndex) {
    const events = this.ctx.events;
    const far = profile.maxRange ?? 300;
    const hits = this.castAll(origin, dir, far, this.physics?.MASK?.ALL);
    const results = [];

    let factor = 1;
    let penetrated = false;
    let surfacesCrossed = 0;
    const maxSurfaces = profile.maxPenetrations ?? 2;
    let endPoint = _v.copy(origin).addScaledVector(dir, far).clone();

    for (let i = 0; i < hits.length; i++) {
      const h = hits[i];
      const dmg = this.damageAt(profile, h.distance) * factor * this.zoneMult(h);

      events?.emit('ballistics:hit', {
        point: h.point, normal: h.normal, surface: h.surface,
        distance: h.distance, entity: h.entity, damage: dmg, penetrated,
      });
      results.push({ ...h, damage: dmg, penetrated });
      endPoint = h.point.clone();

      if (h.entity && typeof h.entity.applyDamage === 'function') {
        try { h.entity.applyDamage(dmg, dir, h.zone); } catch { /* ai may still be a stub */ }
      }

      if (surfacesCrossed >= maxSurfaces) break;
      const thickness = this.thicknessAt(hits, i, dir);
      const k = PENETRATION[h.surface] ?? PENETRATION.default;
      const budget = profile.penetration ?? 22;
      const loss = Math.exp(-(thickness * k) / Math.max(1e-3, budget));
      factor *= loss;
      surfacesCrossed++;
      penetrated = true;
      if (factor < 0.06) break;
    }

    events?.emit('ballistics:tracer', {
      from: origin.clone(), to: endPoint, speed: profile.tracerSpeed ?? 420,
    });
    return results;
  }

  /** Thickness of surface `i` by looking for the matching exit face. */
  thicknessAt(hits, i, dir) {
    const h = hits[i];
    for (let j = i + 1; j < hits.length; j++) {
      const n = hits[j];
      const backFace = n.normal.dot(dir) > 0;
      if (n.object === h.object || backFace) return Math.max(0.001, n.distance - h.distance);
    }
    return ASSUMED_THICKNESS[h.surface] ?? ASSUMED_THICKNESS.default;
  }

  zoneMult(h) {
    if (!h.zone) return 1;
    return HIT_ZONE_MULT[h.zone] ?? 1;
  }

  /** Damage after range falloff. Two-stop linear falloff, as COD uses. */
  damageAt(profile, distance) {
    const d = profile.damage ?? 30;
    const n0 = profile.falloffStart ?? 20;
    const n1 = profile.falloffEnd ?? 55;
    const minMul = profile.minDamageMul ?? 0.45;
    if (distance <= n0) return d;
    if (distance >= n1) return d * minMul;
    const t = (distance - n0) / (n1 - n0);
    return d * (1 + (minMul - 1) * t);
  }

  /* ---------------- projectiles (launcher / heavy sniper class) -------------- */

  spawnProjectile(origin, dir, profile, seed, shotIndex) {
    const p = {
      pos: origin.clone(),
      prev: origin.clone(),
      vel: dir.clone().multiplyScalar(profile.muzzleVelocity ?? 250),
      drag: profile.drag ?? 0.0016,
      gravity: profile.projectileGravity ?? 9.81,
      life: profile.projectileLife ?? 6,
      profile, seed, shotIndex,
    };
    this.projectiles.push(p);
    return p;
  }

  stepProjectiles(dt) {
    const events = this.ctx.events;
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.prev.copy(p.pos);
      const speed = p.vel.length();
      p.vel.addScaledVector(p.vel, -p.drag * speed * dt);
      p.vel.y -= p.gravity * dt;
      p.pos.addScaledVector(p.vel, dt);
      p.life -= dt;

      const seg = _v.copy(p.pos).sub(p.prev);
      const len = seg.length();
      if (len > 1e-5) {
        seg.multiplyScalar(1 / len);
        const hits = this.castAll(p.prev, seg, len, this.physics?.MASK?.ALL);
        if (hits.length) {
          const h = hits[0];
          const dmg = this.damageAt(p.profile, p.prev.distanceTo(h.point)) * this.zoneMult(h);
          events?.emit('ballistics:hit', {
            point: h.point, normal: h.normal, surface: h.surface,
            distance: h.distance, entity: h.entity, damage: dmg, penetrated: false,
          });
          if (p.profile.explosive) {
            events?.emit('fx:explosion', {
              position: h.point.clone(), radius: p.profile.blastRadius ?? 4, power: p.profile.blastPower ?? 1,
            });
          }
          this.projectiles.splice(i, 1);
          continue;
        }
      }
      events?.emit('ballistics:tracer', { from: p.prev.clone(), to: p.pos.clone(), speed });
      if (p.life <= 0) this.projectiles.splice(i, 1);
    }
  }

  /** Deterministic cone spread: same seed + shot index -> same direction. */
  static applySpread(dir, halfAngle, seed, shotIndex, out) {
    out = out || new THREE.Vector3();
    out.copy(dir).normalize();
    if (halfAngle <= 1e-6) return out;
    const r = makeRng(shotHash(seed ^ 0x51ed, shotIndex));
    const a = r() * Math.PI * 2;
    const rad = Math.sqrt(r()) * halfAngle;
    // build a basis around dir
    const up = Math.abs(out.y) > 0.94 ? _n.set(1, 0, 0) : _n.set(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(out, up).normalize();
    const realUp = new THREE.Vector3().crossVectors(right, out).normalize();
    out.addScaledVector(right, Math.cos(a) * Math.tan(rad));
    out.addScaledVector(realUp, Math.sin(a) * Math.tan(rad));
    return out.normalize();
  }
}
